import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { X, Play, Image, Film, Music, ChevronLeft, ChevronRight, Camera, Upload, Loader2, CheckCircle } from 'lucide-react';

const Gallery = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch('/api/gallery/');
        if (response.ok) setGalleryItems(await response.json());
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const categories = ['All', ...new Set(galleryItems.map(img => img.category?.en || img.category || 'General'))];

  const filtered = galleryItems.filter(item => {
    const catMatch = activeCategory === 'All' || (item.category?.en || item.category || 'General') === activeCategory;
    const mediaMatch = mediaFilter === 'all' || (item.media_type || 'image') === mediaFilter;
    return catMatch && mediaMatch;
  });

  const isVideo = (item) => (item.media_type === 'video') || /\.(mp4|webm|ogg|mov|avi)$/i.test(item.url || '');
  const isAudio = (item) => (item.media_type === 'audio') || /\.(mp3|wav|aac|flac|m4a|opus)$/i.test(item.url || '');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="gallery-page">
      <section className="bg-party-green py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t({ en: 'Photo & Video Gallery', bn: 'ফটো ও ভিডিও গ্যালারি' })}
          </h1>
          <p className="text-lg text-green-100 max-w-3xl mx-auto">
            {t({ en: 'Glimpses from our journey, events, and community activities.', bn: 'আমাদের যাত্রা, ইভেন্ট এবং সামাজিক কার্যক্রমের ঝলক।' })}
          </p>
          <p className="text-green-200 mt-2">{filtered.length} {t({ en: 'items', bn: 'আইটেম' })}</p>
          <Button
            onClick={() => setShowSubmit(true)}
            className="mt-4 bg-white text-party-green hover:bg-green-50 font-semibold"
            data-testid="submit-photos-btn"
          >
            <Camera className="h-4 w-4 mr-2" />
            {t({ en: 'Submit Your Photos', bn: 'আপনার ছবি জমা দিন' })}
          </Button>
        </div>
      </section>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Media type filter */}
        <div className="flex justify-center gap-2 mb-4">
          {[
            { key: 'all', label: { en: 'All Media', bn: 'সকল মিডিয়া' }, icon: null },
            { key: 'image', label: { en: 'Photos', bn: 'ছবি' }, icon: Image },
            { key: 'video', label: { en: 'Videos', bn: 'ভিডিও' }, icon: Film },
            { key: 'audio', label: { en: 'Audio', bn: 'অডিও' }, icon: Music },
          ].map(f => (
            <Button
              key={f.key}
              size="sm"
              variant={mediaFilter === f.key ? 'default' : 'outline'}
              className={mediaFilter === f.key ? 'bg-party-green hover:bg-party-green-600' : ''}
              onClick={() => setMediaFilter(f.key)}
              data-testid={`media-filter-${f.key}`}
            >
              {f.icon && <f.icon className="h-4 w-4 mr-1" />}
              {t(f.label)}
            </Button>
          ))}
        </div>
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map(cat => (
            <Button
              key={cat}
              size="sm"
              variant={activeCategory === cat ? 'default' : 'outline'}
              className={activeCategory === cat ? 'bg-party-red hover:bg-party-red-600' : 'hover:border-party-red hover:text-party-red'}
              onClick={() => setActiveCategory(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Gallery Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Film className="h-16 w-16 mx-auto mb-4" />
              <p>{t({ en: 'No items found for this filter', bn: 'এই ফিল্টারের জন্য কোনো আইটেম পাওয়া যায়নি' })}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map((item) => (
                <div
                  key={item._id}
                  className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`gallery-item-${item._id}`}
                >
                  <div className="aspect-square overflow-hidden bg-gray-200">
                    {isVideo(item) ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800 relative">
                        <video src={item.url} className="w-full h-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center">
                            <Play className="h-7 w-7 text-party-red ml-1" />
                          </div>
                        </div>
                      </div>
                    ) : isAudio(item) ? (
                      <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-300 flex flex-col items-center justify-center p-4">
                        <Music className="h-12 w-12 text-purple-600 mb-3" />
                        <p className="text-purple-800 text-xs font-medium text-center truncate w-full">{t(item.caption)}</p>
                      </div>
                    ) : (
                      <img
                        src={item.url}
                        alt={t(item.caption)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white text-sm font-medium line-clamp-2">{t(item.caption)}</p>
                      {isVideo(item) && <span className="inline-block mt-1 text-xs bg-blue-500 px-2 py-0.5 rounded text-white">Video</span>}
                      {isAudio(item) && <span className="inline-block mt-1 text-xs bg-purple-500 px-2 py-0.5 rounded text-white">Audio</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Photo Submission Modal */}
      {showSubmit && (
        <SubmitPhotosModal
          onClose={() => setShowSubmit(false)}
          onSuccess={() => {
            setShowSubmit(false);
            toast({ title: t({ en: 'Photos Submitted!', bn: 'ছবি জমা দেওয়া হয়েছে!' }), description: t({ en: 'Your photos will appear after admin review.', bn: 'অ্যাডমিন পর্যালোচনার পর আপনার ছবি দেখা যাবে।' }) });
          }}
          t={t}
        />
      )}

      {/* Fullscreen Slideshow */}
      {selectedItem && (() => {
        const imageItems = filtered.filter(i => !isVideo(i) && !isAudio(i));
        const currentIdx = imageItems.findIndex(i => i._id === selectedItem._id);
        const isSlideshow = currentIdx !== -1;
        const goPrev = () => { if (isSlideshow && currentIdx > 0) setSelectedItem(imageItems[currentIdx - 1]); };
        const goNext = () => { if (isSlideshow && currentIdx < imageItems.length - 1) setSelectedItem(imageItems[currentIdx + 1]); };

        return (
          <SlideshowOverlay
            item={selectedItem}
            isVideo={isVideo(selectedItem)}
            isAudio={isAudio(selectedItem)}
            isSlideshow={isSlideshow}
            currentIdx={currentIdx}
            total={imageItems.length}
            onClose={() => setSelectedItem(null)}
            onPrev={goPrev}
            onNext={goNext}
            t={t}
          />
        );
      })()}
    </div>
  );
};

const SubmitPhotosModal = ({ onClose, onSuccess, t }) => {
  const [files, setFiles] = useState([]);
  const [name, setName] = useState('');
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('Rally');
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const categories = ['Rally', 'Anniversary', 'Community Service', 'Cultural', 'Meeting', 'Other'];

  const handleFiles = (newFiles) => {
    const valid = Array.from(newFiles).filter(f => /\.(jpg|jpeg|png|webp|gif|mp4|mov)$/i.test(f.name)).slice(0, 10 - files.length);
    setFiles(prev => [...prev, ...valid].slice(0, 10));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length || !name.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('submitter_name', name.trim());
      formData.append('caption', caption.trim());
      formData.append('category', category);
      const res = await fetch('/api/gallery/submit', { method: 'POST', body: formData });
      if (res.ok) {
        setDone(true);
        setTimeout(onSuccess, 1500);
      } else {
        const err = await res.json();
        alert(err.detail || 'Upload failed');
      }
    } catch { alert('Connection error'); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="submit-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="submit-modal">
        <div className="bg-gradient-to-r from-party-green to-emerald-600 p-5 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <Camera className="h-5 w-5" />
            {t({ en: 'Submit Your Photos', bn: 'আপনার ছবি জমা দিন' })}
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {done ? (
          <div className="p-10 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t({ en: 'Thank You!', bn: 'ধন্যবাদ!' })}</h3>
            <p className="text-gray-500">{t({ en: 'Your photos have been submitted for review.', bn: 'আপনার ছবি পর্যালোচনার জন্য জমা দেওয়া হয়েছে।' })}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <Label>{t({ en: 'Your Name', bn: 'আপনার নাম' })} *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} required className="mt-1" data-testid="submit-name" placeholder={t({ en: 'Enter your full name', bn: 'আপনার পূর্ণ নাম লিখুন' })} />
            </div>

            <div>
              <Label>{t({ en: 'Caption / Description', bn: 'ক্যাপশন / বিবরণ' })}</Label>
              <Input value={caption} onChange={e => setCaption(e.target.value)} className="mt-1" data-testid="submit-caption" placeholder={t({ en: 'What are these photos about?', bn: 'এই ছবিগুলো কী সম্পর্কে?' })} />
            </div>

            <div>
              <Label>{t({ en: 'Category', bn: 'বিভাগ' })}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {categories.map(cat => (
                  <button key={cat} type="button" onClick={() => setCategory(cat)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${category === cat ? 'bg-party-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    data-testid={`submit-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
                  >{cat}</button>
                ))}
              </div>
            </div>

            {/* Dropzone */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center cursor-pointer hover:border-party-green transition-colors"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              data-testid="submit-dropzone"
            >
              <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime" multiple className="hidden" onChange={e => handleFiles(e.target.files)} data-testid="submit-file-input" />
              <Upload className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t({ en: 'Click or drag photos here (max 10)', bn: 'এখানে ক্লিক করুন বা ছবি টেনে আনুন (সর্বোচ্চ ১০টি)' })}</p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP, MP4 — max 20MB each</p>
            </div>

            {/* File Previews */}
            {files.length > 0 && (
              <div className="grid grid-cols-4 gap-2" data-testid="submit-previews">
                {files.map((f, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group">
                    {f.type.startsWith('video') ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800"><Play className="h-6 w-6 text-white" /></div>
                    ) : (
                      <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    )}
                    <button type="button" onClick={() => removeFile(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Button type="submit" className="w-full bg-party-green hover:bg-green-700" disabled={!files.length || !name.trim() || uploading} data-testid="submit-upload-btn">
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t({ en: 'Uploading...', bn: 'আপলোড হচ্ছে...' })}</> : <><Upload className="h-4 w-4 mr-2" /> {t({ en: `Submit ${files.length} Photo${files.length !== 1 ? 's' : ''}`, bn: `${files.length}টি ছবি জমা দিন` })}</>}
            </Button>

            <p className="text-xs text-center text-gray-400">{t({ en: 'Photos will be reviewed by admin before appearing in the gallery.', bn: 'গ্যালারিতে প্রদর্শিত হওয়ার আগে ছবিগুলো অ্যাডমিন দ্বারা পর্যালোচনা করা হবে।' })}</p>
          </form>
        )}
      </div>
    </div>
  );
};

const SlideshowOverlay = ({ item, isVideo, isAudio, isSlideshow, currentIdx, total, onClose, onPrev, onNext, t }) => {
  const touchStart = useRef(null);

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (isSlideshow && e.key === 'ArrowLeft') onPrev();
    if (isSlideshow && e.key === 'ArrowRight') onNext();
  }, [onClose, onPrev, onNext, isSlideshow]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKey); document.body.style.overflow = ''; };
  }, [handleKey]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? onNext() : onPrev(); }
    touchStart.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="slideshow-overlay"
    >
      {/* Close */}
      <button className="absolute top-4 right-4 z-50 text-white/70 hover:text-white transition-colors" onClick={onClose} data-testid="slideshow-close">
        <X className="h-8 w-8" />
      </button>

      {/* Counter */}
      {isSlideshow && (
        <div className="absolute top-4 left-4 z-50 text-white/60 text-sm font-medium bg-black/40 px-3 py-1 rounded-full" data-testid="slideshow-counter">
          {currentIdx + 1} / {total}
        </div>
      )}

      {/* Prev */}
      {isSlideshow && currentIdx > 0 && (
        <button
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          data-testid="slideshow-prev"
        >
          <ChevronLeft className="h-7 w-7" />
        </button>
      )}

      {/* Next */}
      {isSlideshow && currentIdx < total - 1 && (
        <button
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          data-testid="slideshow-next"
        >
          <ChevronRight className="h-7 w-7" />
        </button>
      )}

      {/* Content */}
      <div className="max-w-6xl max-h-[90vh] relative px-4" onClick={e => e.stopPropagation()}>
        {isVideo ? (
          <video src={item.url} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg mx-auto" data-testid="slideshow-video" />
        ) : isAudio ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Music className="h-24 w-24 text-purple-500 mx-auto mb-6" />
            <audio src={item.url} controls autoPlay className="w-96" data-testid="slideshow-audio" />
          </div>
        ) : (
          <img src={item.url} alt={t(item.caption)} className="max-w-full max-h-[85vh] object-contain rounded-lg mx-auto select-none" draggable={false} data-testid="slideshow-image" />
        )}
        <div className="text-center mt-4">
          <p className="text-white text-lg font-medium">{t(item.caption)}</p>
          <p className="text-gray-400 text-sm mt-1">{t(item.category)}</p>
        </div>
      </div>
    </div>
  );
};

export default Gallery;
